/**
 * Nightclub Canvas Drawing Utilities
 * Extracted from Nightclub.js to reduce file size
 * OPTIMIZED: Static elements cached, reduced per-frame calculations
 */

// Cache for static elements (stars, buildings) - generated once
let cachedStars = null;
let cachedBuildings = null;

// Generate static star positions (only once)
const generateStars = (W, H) => {
  const stars = [];
  const starCount = 50; // Reduced from 80
  for (let i = 0; i < starCount; i++) {
    stars.push({
      x: (i * 137.5) % W,
      y: (i * 73.3) % (H * 0.4),
      size: (i % 3) * 0.4 + 0.5, // Smaller range
      colorIndex: i % 4,
      phaseOffset: i * 0.7
    });
  }
  return stars;
};

// Draw starry night sky for rooftop - OPTIMIZED
export const drawStarrySky = (ctx, W, H, phase) => {
  // Beautiful gradient night sky
  const skyGradient = ctx.createLinearGradient(0, 0, 0, H * 0.5);
  skyGradient.addColorStop(0, '#0a0a1a');
  skyGradient.addColorStop(0.5, '#151535');
  skyGradient.addColorStop(1, '#252550');
  ctx.fillStyle = skyGradient;
  ctx.fillRect(0, 0, W, H * 0.5);
  
  // Generate stars cache if needed
  if (!cachedStars) {
    cachedStars = generateStars(W, H);
  }
  
  // Draw cached stars with simple twinkle
  const colors = ['255,255,255', '255,240,200', '200,220,255', '255,200,200'];
  const twinkleBase = Math.sin(phase * 0.05);
  
  cachedStars.forEach(star => {
    const twinkle = 0.5 + twinkleBase * 0.3 + (star.phaseOffset % 1) * 0.2;
    ctx.fillStyle = `rgba(${colors[star.colorIndex]},${twinkle})`;
    ctx.fillRect(star.x - star.size/2, star.y - star.size/2, star.size, star.size);
  });
  
  // Moon - simplified (no glow gradient per frame)
  const moonX = W - 100;
  const moonY = 70;
  ctx.fillStyle = '#F5F5DC';
  ctx.beginPath();
  ctx.arc(moonX, moonY, 35, 0, Math.PI * 2);
  ctx.fill();
  
  // Simple moon craters
  ctx.fillStyle = 'rgba(200, 195, 180, 0.4)';
  ctx.beginPath();
  ctx.arc(moonX - 10, moonY - 8, 6, 0, Math.PI * 2);
  ctx.arc(moonX + 12, moonY + 5, 5, 0, Math.PI * 2);
  ctx.fill();
};

// Draw city skyline view - OPTIMIZED with fewer buildings and simplified windows
export const drawCityView = (ctx, W, H, phase) => {
  const skylineY = H * 0.35;
  
  // Simplified buildings - fewer, no per-frame window calculations
  const buildings = [
    { x: 30, w: 40, h: 100 },
    { x: 100, w: 50, h: 130 },
    { x: 180, w: 35, h: 80 },
    { x: W/2 - 35, w: 70, h: 150 },
    { x: W - 220, w: 45, h: 95 },
    { x: W - 130, w: 55, h: 120 },
    { x: W - 60, w: 40, h: 75 },
  ];
  
  // Draw all buildings as silhouettes first
  ctx.fillStyle = '#12122a';
  buildings.forEach(b => {
    ctx.fillRect(b.x, skylineY - b.h, b.w, b.h + 30);
  });
  
  // Simplified window pattern - static grid, fewer windows
  ctx.fillStyle = 'rgba(255, 220, 100, 0.7)';
  buildings.forEach(b => {
    for (let wy = skylineY - b.h + 15; wy < skylineY - 10; wy += 18) {
      for (let wx = b.x + 6; wx < b.x + b.w - 6; wx += 14) {
        // Simple static lit pattern
        if ((wx + wy) % 3 !== 0) {
          ctx.fillRect(wx, wy, 4, 6);
        }
      }
    }
  });
  
  // Ground/terrace floor
  ctx.fillStyle = '#1a2f1a';
  ctx.fillRect(0, skylineY + 10, W, H - skylineY);
  
  // Simple railing
  ctx.fillStyle = '#4a4a5a';
  ctx.fillRect(0, skylineY + 12, W, 4);
};

// Draw string lights - OPTIMIZED with fewer lights and simplified glow
export const drawStringLights = (ctx, W, H, phase) => {
  const lightY = 70;
  const lightCount = 10; // Reduced from 15
  const spacing = (W - 80) / lightCount;
  
  // Single strand only
  ctx.strokeStyle = '#2a2a3a';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.moveTo(40, lightY);
  for (let i = 0; i <= lightCount; i++) {
    const x = 40 + i * spacing;
    const sag = Math.sin((i / lightCount) * Math.PI) * 18;
    ctx.lineTo(x, lightY + sag);
  }
  ctx.stroke();
  
  // Light bulbs - simplified colors
  const colors = ['#FFD700', '#FF6B6B', '#4ECDC4', '#FFE66D', '#95E1D3'];
  
  for (let i = 0; i <= lightCount; i++) {
    const x = 40 + i * spacing;
    const sag = Math.sin((i / lightCount) * Math.PI) * 18;
    const bulbY = lightY + sag + 8;
    const color = colors[i % colors.length];
    
    // Simple glow without shadowBlur (expensive!)
    ctx.fillStyle = color;
    ctx.globalAlpha = 0.3;
    ctx.beginPath();
    ctx.arc(x, bulbY, 10, 0, Math.PI * 2);
    ctx.fill();
    
    ctx.globalAlpha = 1;
    ctx.beginPath();
    ctx.arc(x, bulbY, 4, 0, Math.PI * 2);
    ctx.fill();
  }
};

// Draw rooftop trees - OPTIMIZED
export const drawRooftopTrees = (ctx, W, H, floorY, phase) => {
  const treePositions = [
    { x: 80, size: 1.0, type: 'palm' },
    { x: W - 100, size: 0.9, type: 'palm' },
  ];
  
  treePositions.forEach(({ x, size, type }) => {
    const treeY = floorY + 55;
    
    // Simple pot
    ctx.fillStyle = '#8B4513';
    ctx.fillRect(x - 15 * size, treeY, 30 * size, 25 * size);
    
    // Pot rim
    ctx.fillStyle = '#A0522D';
    ctx.fillRect(x - 18 * size, treeY - 3, 36 * size, 6);
    
    // Palm trunk
    ctx.fillStyle = '#4a3728';
    ctx.fillRect(x - 5 * size, treeY - 55 * size, 10 * size, 58 * size);
    
    // Palm fronds - simplified
    ctx.strokeStyle = '#228B22';
    ctx.lineWidth = 3 * size;
    for (let f = 0; f < 6; f++) {
      const angle = (f / 6) * Math.PI * 2;
      ctx.beginPath();
      ctx.moveTo(x, treeY - 55 * size);
      const endX = x + Math.cos(angle) * 40 * size;
      const endY = treeY - 55 * size + Math.sin(angle) * 25 * size + 15 * size;
      ctx.quadraticCurveTo(
        x + Math.cos(angle) * 20 * size,
        treeY - 55 * size + Math.sin(angle) * 12 * size,
        endX, endY
      );
      ctx.stroke();
    }
  });
};

// NEW: Draw rooftop furniture (loungers, bar) - P1 Feature
export const drawRooftopFurniture = (ctx, W, H, floorY, phase) => {
  const furnitureY = floorY + 60;
  
  // === LOUNGE CHAIRS (Left side) ===
  const drawLounger = (x, angle = 0) => {
    ctx.save();
    ctx.translate(x, furnitureY);
    
    // Chair base/legs
    ctx.fillStyle = '#2a2a2a';
    ctx.fillRect(-30, 15, 60, 8);
    ctx.fillRect(-28, 23, 8, 12);
    ctx.fillRect(20, 23, 8, 12);
    
    // Reclining back
    ctx.fillStyle = '#1a1a1a';
    ctx.beginPath();
    ctx.moveTo(-28, 15);
    ctx.lineTo(-28, -15);
    ctx.lineTo(-5, -20);
    ctx.lineTo(28, 10);
    ctx.lineTo(28, 15);
    ctx.closePath();
    ctx.fill();
    
    // Cushion
    ctx.fillStyle = '#4a3060';
    ctx.beginPath();
    ctx.moveTo(-25, 12);
    ctx.lineTo(-25, -12);
    ctx.lineTo(-3, -17);
    ctx.lineTo(25, 8);
    ctx.lineTo(25, 12);
    ctx.closePath();
    ctx.fill();
    
    // Cushion highlight
    ctx.fillStyle = '#5a4070';
    ctx.beginPath();
    ctx.moveTo(-22, 8);
    ctx.lineTo(-22, -8);
    ctx.lineTo(0, -12);
    ctx.lineTo(15, 5);
    ctx.lineTo(15, 8);
    ctx.closePath();
    ctx.fill();
    
    ctx.restore();
  };
  
  // Two loungers on left
  drawLounger(120);
  drawLounger(220);
  
  // === BAR AREA (Right side) ===
  const barX = W - 180;
  
  // Bar counter
  ctx.fillStyle = '#1a1a1a';
  ctx.fillRect(barX - 60, furnitureY - 25, 120, 35);
  
  // Bar top (wood grain look)
  const barTopGrad = ctx.createLinearGradient(barX - 60, furnitureY - 28, barX + 60, furnitureY - 28);
  barTopGrad.addColorStop(0, '#5c3d2e');
  barTopGrad.addColorStop(0.5, '#7a5240');
  barTopGrad.addColorStop(1, '#5c3d2e');
  ctx.fillStyle = barTopGrad;
  ctx.fillRect(barX - 65, furnitureY - 30, 130, 8);
  
  // Bar front panel with subtle pattern
  ctx.fillStyle = '#252525';
  ctx.fillRect(barX - 55, furnitureY - 22, 110, 28);
  
  // Neon accent strip
  const neonGlow = 0.5 + Math.sin(phase * 0.03) * 0.3;
  ctx.fillStyle = `rgba(147, 51, 234, ${neonGlow})`;
  ctx.fillRect(barX - 55, furnitureY - 2, 110, 3);
  
  // Bar stools
  const drawStool = (stoolX) => {
    // Stool leg
    ctx.fillStyle = '#3a3a3a';
    ctx.fillRect(stoolX - 3, furnitureY + 5, 6, 25);
    
    // Seat
    ctx.fillStyle = '#2a2a2a';
    ctx.beginPath();
    ctx.arc(stoolX, furnitureY + 5, 12, 0, Math.PI * 2);
    ctx.fill();
    
    // Seat cushion
    ctx.fillStyle = '#4a3060';
    ctx.beginPath();
    ctx.arc(stoolX, furnitureY + 3, 10, 0, Math.PI * 2);
    ctx.fill();
  };
  
  drawStool(barX - 35);
  drawStool(barX);
  drawStool(barX + 35);
  
  // Bottles behind bar (decorative)
  const bottleColors = ['#2ecc71', '#e74c3c', '#3498db', '#f39c12', '#9b59b6'];
  for (let i = 0; i < 5; i++) {
    const bx = barX - 40 + i * 22;
    ctx.fillStyle = bottleColors[i];
    ctx.fillRect(bx, furnitureY - 50, 8, 18);
    ctx.fillRect(bx + 2, furnitureY - 55, 4, 6);
  }
  
  // Glasses on bar
  ctx.fillStyle = 'rgba(255, 255, 255, 0.3)';
  ctx.fillRect(barX - 20, furnitureY - 35, 6, 10);
  ctx.fillRect(barX + 10, furnitureY - 33, 5, 8);
  
  // === BARTENDER NPC ===
  const bartenderX = barX;
  const bartenderY = furnitureY - 55;
  
  // Body
  ctx.fillStyle = '#1a1a1a'; // Dark vest
  ctx.beginPath();
  ctx.ellipse(bartenderX, bartenderY + 15, 12, 18, 0, 0, Math.PI * 2);
  ctx.fill();
  
  // White shirt underneath
  ctx.fillStyle = '#f0f0f0';
  ctx.beginPath();
  ctx.moveTo(bartenderX - 5, bartenderY + 5);
  ctx.lineTo(bartenderX + 5, bartenderY + 5);
  ctx.lineTo(bartenderX + 3, bartenderY + 25);
  ctx.lineTo(bartenderX - 3, bartenderY + 25);
  ctx.closePath();
  ctx.fill();
  
  // Head
  ctx.fillStyle = '#D4A574'; // Skin tone
  ctx.beginPath();
  ctx.arc(bartenderX, bartenderY - 5, 10, 0, Math.PI * 2);
  ctx.fill();
  
  // Hair (slicked back)
  ctx.fillStyle = '#1a1a1a';
  ctx.beginPath();
  ctx.arc(bartenderX, bartenderY - 8, 9, Math.PI, 0, false);
  ctx.fill();
  
  // Bowtie
  ctx.fillStyle = '#DC2626';
  ctx.beginPath();
  ctx.moveTo(bartenderX - 6, bartenderY + 5);
  ctx.lineTo(bartenderX, bartenderY + 8);
  ctx.lineTo(bartenderX + 6, bartenderY + 5);
  ctx.lineTo(bartenderX, bartenderY + 2);
  ctx.closePath();
  ctx.fill();
  
  // Simple face
  ctx.fillStyle = '#333';
  ctx.fillRect(bartenderX - 4, bartenderY - 7, 2, 2); // Left eye
  ctx.fillRect(bartenderX + 2, bartenderY - 7, 2, 2); // Right eye
  
  // Smile
  ctx.strokeStyle = '#333';
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(bartenderX, bartenderY - 2, 4, 0.1 * Math.PI, 0.9 * Math.PI);
  ctx.stroke();
  
  // Arms (one holding glass)
  ctx.fillStyle = '#1a1a1a';
  ctx.fillRect(bartenderX - 18, bartenderY + 8, 8, 4); // Left arm
  ctx.fillRect(bartenderX + 10, bartenderY + 8, 8, 4); // Right arm
  
  // Cocktail shaker in right hand
  const shakerBob = Math.sin(phase * 0.08) * 2;
  ctx.fillStyle = '#C0C0C0';
  ctx.fillRect(bartenderX + 16, bartenderY + shakerBob, 6, 14);
  ctx.fillStyle = '#A0A0A0';
  ctx.fillRect(bartenderX + 17, bartenderY + shakerBob - 4, 4, 5);
  
  // "TALK TO ME" indicator with pulsing glow
  const pulseAlpha = 0.6 + Math.sin(phase * 0.06) * 0.4;
  ctx.fillStyle = `rgba(255, 215, 0, ${pulseAlpha})`;
  ctx.font = 'bold 11px sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText('TAP TO ORDER', bartenderX, bartenderY - 25);
  
  // Subtle clickable area indicator (pulsing border)
  ctx.strokeStyle = `rgba(255, 215, 0, ${pulseAlpha * 0.3})`;
  ctx.lineWidth = 2;
  ctx.setLineDash([5, 5]);
  ctx.strokeRect(bartenderX - 40, bartenderY - 50, 80, 100);
  ctx.setLineDash([]);
  
  // Small speech bubble
  ctx.fillStyle = `rgba(255, 255, 255, ${pulseAlpha * 0.8})`;
  ctx.beginPath();
  ctx.arc(bartenderX, bartenderY - 35, 12, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = '#333';
  ctx.font = '14px sans-serif';
  ctx.fillText('🍹', bartenderX, bartenderY - 31);
};

// Get bartender clickable area coordinates (for use in Nightclub.js)
export const getBartenderHitbox = (W, H, floorY) => {
  const furnitureY = floorY + 60;
  const barX = W - 180;
  const bartenderX = barX;
  const bartenderY = furnitureY - 55;
  
  // Expanded hitbox for easier clicking
  return {
    x: bartenderX - 40,
    y: bartenderY - 50,
    width: 80,
    height: 100
  };
};

// Draw lounge seating for chill zone
export const drawLoungeSeating = (ctx, W, H, floorY, accentColor) => {
  // Left curved couch
  ctx.fillStyle = '#2d2d2d';
  ctx.beginPath();
  ctx.moveTo(30, floorY + 50);
  ctx.quadraticCurveTo(80, floorY + 80, 130, floorY + 50);
  ctx.lineTo(130, floorY + 100);
  ctx.lineTo(30, floorY + 100);
  ctx.closePath();
  ctx.fill();
  
  // Cushion
  ctx.fillStyle = accentColor;
  ctx.globalAlpha = 0.3;
  ctx.beginPath();
  ctx.moveTo(40, floorY + 55);
  ctx.quadraticCurveTo(80, floorY + 75, 120, floorY + 55);
  ctx.lineTo(120, floorY + 70);
  ctx.lineTo(40, floorY + 70);
  ctx.closePath();
  ctx.fill();
  ctx.globalAlpha = 1;
  
  // Right curved couch
  ctx.fillStyle = '#2d2d2d';
  ctx.beginPath();
  ctx.moveTo(W - 130, floorY + 50);
  ctx.quadraticCurveTo(W - 80, floorY + 80, W - 30, floorY + 50);
  ctx.lineTo(W - 30, floorY + 100);
  ctx.lineTo(W - 130, floorY + 100);
  ctx.closePath();
  ctx.fill();
  
  // Cushion
  ctx.fillStyle = accentColor;
  ctx.globalAlpha = 0.3;
  ctx.beginPath();
  ctx.moveTo(W - 120, floorY + 55);
  ctx.quadraticCurveTo(W - 80, floorY + 75, W - 40, floorY + 55);
  ctx.lineTo(W - 40, floorY + 70);
  ctx.lineTo(W - 120, floorY + 70);
  ctx.closePath();
  ctx.fill();
  ctx.globalAlpha = 1;
};

// Draw chandelier for VIP room
export const drawChandelier = (ctx, x, y, phase) => {
  // Main body
  ctx.fillStyle = '#FFD700';
  ctx.beginPath();
  ctx.arc(x, y, 15, 0, Math.PI * 2);
  ctx.fill();
  
  // Hanging crystals - simplified (no shadowBlur)
  const crystalGlow = 0.5 + Math.sin(phase * 0.05) * 0.3;
  ctx.fillStyle = `rgba(255, 215, 0, ${crystalGlow})`;
  for (let i = 0; i < 6; i++) {
    const angle = (i / 6) * Math.PI * 2;
    const cx = x + Math.cos(angle) * 22;
    const cy = y + Math.sin(angle) * 8 + 18;
    
    ctx.beginPath();
    ctx.moveTo(cx, cy - 6);
    ctx.lineTo(cx + 3, cy);
    ctx.lineTo(cx, cy + 6);
    ctx.lineTo(cx - 3, cy);
    ctx.closePath();
    ctx.fill();
  }
};
